import { useMemo } from "react";
import { FaApple, FaMobileAlt, FaTabletAlt, FaTv, FaLaptop } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { deviceLastSeenLabel } from "../utilities/deviceLastSeen.js";
import { softwareUpdateVersionLabel } from "../services/softwareUpdateService";

const DEVICE_TYPE_ICONS = {
  mac: FaLaptop,
  iphone: FaMobileAlt,
  ipad: FaTabletAlt,
  appletv: FaTv,
};

export default function AllDeviceRow({
  device,
  isSelected,
  onToggleSelect,
  onClick,
  suPolicy,
  suBusy,
  onResetSoftwareUpdate,
  onAssignUser,
}) {
  const navigate = useNavigate();

  const {
    lastCheckin,
    deviceTypeIcon,
    linkAddress,
    osVersion,
    name,
    locationLabel,
    assetTag
  } = useMemo(() => {
    const lastCheckin = deviceLastSeenLabel(device);

    // Determine device type icon (mac, tablet, phone, tv)
    const deviceTypeIcon = (() => {
      const IconComponent = DEVICE_TYPE_ICONS[device.type?.toLowerCase()] || FaApple;
      return <IconComponent className="text-gray-700" />;
    })();

    const linkAddress = `/${device.type}/${device.UDID || device.SerialNumber}`;
    
    const osVersion = device.QueryResponses?.OSVersion || device.OSVersion;
    const name = device.QueryResponses?.DeviceName;
    const locationLabel = device.location
      ? `${device.location.schoolNumber || ''} ${device.location.name || ''}`.trim() || '-'
      : '-';
    const assetTag = device.assetTag || '-';

    return {
      lastCheckin,
      deviceTypeIcon,
      linkAddress,
      osVersion,
      name,
      locationLabel,
      assetTag
    };
  }, [device]);

  const handleRowClick = (e) => {
    if (e.target.closest('input[type="checkbox"]')) return;
    onClick ? onClick() : navigate(linkAddress);
  };

  return (
    <tr 
      className={`cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'table-active' : ''}`}
      onClick={handleRowClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleRowClick(e);
        }
      }}
    >
      {onToggleSelect != null && (
        <td onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="form-check-input"
            checked={!!isSelected}
            onChange={() => onToggleSelect(device)}
            onClick={(e) => e.stopPropagation()}
          />
        </td>
      )}
      <td className="py-2">{deviceTypeIcon}</td>
      <td className="py-2">{device.SerialNumber}</td>
      <td className="py-2">{name}</td>
      <td className="py-2">{device.ProductName}</td>
      <td className="py-2">{locationLabel}</td>
      <td className="py-2">{assetTag}</td>
      <td className="py-2" onClick={(e) => onAssignUser ? e.stopPropagation() : undefined}>
        {device.assignedDirectoryUser?.name || "—"}
        {onAssignUser ? (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 ms-1 align-baseline"
            onClick={(event) => {
              event.stopPropagation();
              onAssignUser(device);
            }}
          >
            zuweisen
          </button>
        ) : null}
      </td>
      <td className="py-2">{osVersion}</td>
      <td className="py-2" onClick={(e) => e.stopPropagation()}>
        {suPolicy?.effectiveIdentifier
          ? softwareUpdateVersionLabel(suPolicy.effectiveIdentifier)
          : (suPolicy?.mode === "none" ? "kein Update" : "—")}
        {suPolicy?.overridden ? (
          <span className="badge text-bg-warning ms-1">Override</span>
        ) : null}
        {suPolicy?.overridden && onResetSoftwareUpdate && device.UDID ? (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 ms-1 align-baseline"
            disabled={suBusy}
            onClick={(event) => onResetSoftwareUpdate(device, event)}
          >
            zurücksetzen
          </button>
        ) : null}
      </td>
      <td className="py-2">{lastCheckin}</td>
    </tr>
  );
}
